import { Grid } from '@chakra-ui/react';
import type { ProgramStatus } from '@fluenceur/types';
import { useListProgram } from '@/hooks/queries/use-list-program';
import ProgramCard from './ProgramCard';
import ProgramCardSkeleton from './ProgramCardSkeleton';
import ProgramsEmptyState from './ProgramsEmptyState';
import ProgramsErrorState from './ProgramsErrorState';

interface ProgramsGridProps {
  status: ProgramStatus;
  onCreate: () => void;
}

const GRID_PROPS = {
  templateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
  gap: '14px',
} as const;

const SKELETON_COUNT = 6;

/**
 * Owns the program list query for the selected status and renders the card grid
 * with loading / error / empty states.
 */
const ProgramsGrid = ({ status, onCreate }: ProgramsGridProps) => {
  const { data, isLoading, isError, error } = useListProgram({ status });

  if (isLoading) {
    return (
      <Grid {...GRID_PROPS}>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <ProgramCardSkeleton key={index} />
        ))}
      </Grid>
    );
  }

  if (isError) {
    return <ProgramsErrorState message={error?.message} />;
  }

  const programs = data ?? [];
  if (programs.length === 0) {
    return <ProgramsEmptyState status={status} onCreate={onCreate} />;
  }

  return (
    <Grid {...GRID_PROPS}>
      {programs.map((program) => (
        <ProgramCard key={program.id} program={program} />
      ))}
    </Grid>
  );
};

export default ProgramsGrid;
